import { createDynamicComponent } from "../../../components-common/_shared";
import {
  InputNumber,
  InputNumberFloat,
  InputNumberInteger,
  InputText,
} from "@components-common/forms";
import { ParameterType } from "./types/parameter.type";
import { useField } from "formik";

/**
 * Functional Component: ParameterTypeInput
 *
 * This component renders the appropriate input field based on the parameter type.
 * It uses a mapper to select the correct input component for types such as NUMBER, INTEGER, and TEXT.
 *
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.type - The type of the parameter.
 * @param {string} [props.id="value"] - The ID for the input field (default is "value").
 * @param {Object} rest - Additional properties passed to the input field.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the appropriate input field for the parameter type.
 */
const mapper = {
  NUMBER: InputNumberFloat,
  INTEGER: InputNumberInteger,
  TEXT: InputText,
};

/**
 * Functional Component: ParameterTypeInput
 *
 * This component renders the appropriate input field based on the parameter type.
 * It uses a mapper to select the correct input component for types such as NUMBER, INTEGER, and TEXT.
 *
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.type - The type of the parameter.
 * @param {string} [props.id="value"] - The ID for the input field (default is "value").
 * @param {Object} rest - Additional properties passed to the input field.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the appropriate input field for the parameter type.
 */
export const ParameterTypeInput = ({
  type,
  id = "value",
  ...rest
}: any = {}) => {
  const resultType = type.value?.toUpperCase() as ParameterType;
  if (type === "NUMBER") {
    return (
      <InputNumberFloat
        id={id}
        {...rest}
        elementClass="w-100 required"
        fixedDecimalScale
        decimalScale={3}
        maxValue={999999}
      />
    );
  }
  const result = mapper[type as "NUMBER" | "INTEGER" | "TEXT"] ? (
    createDynamicComponent({
      component: mapper[type as "NUMBER" | "INTEGER" | "TEXT"],
      props: { id, ...rest, elementClass: "w-100 required" },
    })
  ) : (
    <></>
  );

  return result;
};
